import { useEffect } from 'react';

function formatLastSuccess(date?: Date) {
	const d = date ?? new Date();
	const yyyy = d.getFullYear();
	const MM = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	const hh = String(d.getHours()).padStart(2, '0');
	const mm = String(d.getMinutes()).padStart(2, '0');
	return `${hh}:${mm}, ${yyyy}/${MM}/${dd}`;
}

export function CountdownInfoModal({
	open,
	onClose,
	lastSuccessAt,
}: {
	open: boolean;
	onClose: () => void;
	lastSuccessAt?: Date;
}) {
	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open, onClose]);

	if (!open) return null;

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="update-modal-title"
			tabIndex={-1}
			aria-hidden="true"
			style={{ backdropFilter: 'blur(2px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
			className="flex overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-[101] justify-center items-center w-full md:inset-0 h-screen max-h-full"
			onClick={onClose}
		>
			<div
				className="relative w-full max-w-3xl rounded-lg bg-white shadow-sm"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div className="flex items-center gap-3 px-6 pt-3 pb-3">
					<span className="text-[#023E65]">
						<svg
							width="32"
							height="32"
							viewBox="0 0 24 25"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							className="shrink-0"
						>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M3 19.5652V15.0652H9V13.5652H3V9.06525H16.5V3.06525C16.5 2.66742 16.342 2.28589 16.0607 2.00459C15.7794 1.72328 15.3978 1.56525 15 1.56525H3C2.60218 1.56525 2.22064 1.72328 1.93934 2.00459C1.65804 2.28589 1.5 2.66742 1.5 3.06525V19.5652C1.5 19.9631 1.65804 20.3446 1.93934 20.6259C2.22064 20.9072 2.60218 21.0652 3 21.0652H9V19.5652H3ZM3 3.06525H15V7.56525H3V3.06525ZM5.25 6.06525C5.66421 6.06525 6 5.72946 6 5.31525C6 4.90103 5.66421 4.56525 5.25 4.56525C4.83579 4.56525 4.5 4.90103 4.5 5.31525C4.5 5.72946 4.83579 6.06525 5.25 6.06525ZM5.25 12.0652C5.66421 12.0652 6 11.7295 6 11.3152C6 10.901 5.66421 10.5652 5.25 10.5652C4.83579 10.5652 4.5 10.901 4.5 11.3152C4.5 11.7295 4.83579 12.0652 5.25 12.0652ZM6 17.3152C6 17.7295 5.66421 18.0652 5.25 18.0652C4.83579 18.0652 4.5 17.7295 4.5 17.3152C4.5 16.901 4.83579 16.5652 5.25 16.5652C5.66421 16.5652 6 16.901 6 17.3152ZM21.0001 14.6251V12.8153H22.5001V17.3153H18.0001V15.8153H19.9322C19.6617 15.1955 19.2286 14.6603 18.6788 14.2665C18.129 13.8728 17.4829 13.635 16.809 13.5784C16.1351 13.5219 15.4584 13.6486 14.8506 13.9452C14.2428 14.2418 13.7266 14.6973 13.3565 15.2633C12.9865 15.8294 12.7764 16.485 12.7485 17.1607C12.7206 17.8364 12.876 18.5071 13.1982 19.1017C13.5203 19.6963 13.9973 20.1928 14.5786 20.5384C15.1599 20.8841 15.8238 21.0661 16.5001 21.0653V22.5653C15.5823 22.5658 14.6805 22.3256 13.8846 21.8687C13.0887 21.4118 12.4265 20.7541 11.9643 19.9613C11.502 19.1685 11.2557 18.2683 11.2501 17.3505C11.2445 16.4328 11.4796 15.5296 11.9321 14.7312C12.3846 13.9328 13.0386 13.267 13.8289 12.8003C14.6191 12.3336 15.5179 12.0824 16.4356 12.0716C17.3533 12.0609 18.2577 12.2911 19.0587 12.7391C19.8596 13.1872 20.529 13.8375 21.0001 14.6251Z"
								fill="currentColor"
							/>
						</svg>
					</span>
					<h2 id="update-modal-title" className="text-[28px] leading-8 font-bold text-[#023E65]">
						Understanding data updates
					</h2>

					<button
						onClick={onClose}
						type="button"
						className="custom-modal-close-btn ms-auto"
						data-modal-hide="default-modal"
					>
						<svg
							className="w-3 h-3"
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 14 14"
						>
							<path
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
							/>
						</svg>
						<span className="sr-only">Close modal</span>
					</button>
				</div>

				<div className="px-6">
					<div className="flex items-start gap-2 rounded border border-[#D9EEF8] bg-[#E3F7FF] px-2 py-2">
						<span className="text-[#535662]">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
								<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
								<path
									d="M12 8h.01M12 11v6"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
							</svg>
						</span>
						<p className="text-[14px] font-regular">
							Last successful data update was at {formatLastSuccess(lastSuccessAt)}
						</p>
					</div>
				</div>

				{/* Body */}
				<div className="px-5 py-5 pb-6">
					<ul className="list-disc pl-3 space-y-2 leading-6 font-regular text-[#434B56] text-[14px]">
						<li>
							Data updates in the app every 15 minutes. Please allow up to 3 minutes for the update
							to complete.
						</li>
						<li>
							If the play bar is actively playing, or not at the start point, new data will not
							automatically display on the map. You can trigger the refresh at any point once a new
							update is available.
						</li>
						<li>
							Updating the data ensures we are using the latest information available from sensors
							in the smart sewers network to predict how the network will react to each strategy.
						</li>
						<li>
							If a data refresh has failed, the system will try again at the next data refresh
							window.
						</li>
					</ul>
				</div>
			</div>
		</div>
	);
}
